const toCamel = (str: string): string => {
  return str.replace(/([_-][a-z0-9])/gi, ($1: string) => {
    return $1.toUpperCase().replace("-", "").replace("_", "");
  });
};

const isArray = function (
  input: Record<string, unknown> | Record<string, unknown>[] | unknown
): input is Record<string, unknown>[] {
  return Array.isArray(input);
};

const isObject = function (
  obj: Record<string, unknown> | Record<string, unknown>[] | unknown
): obj is Record<string, unknown> {
  return (
    obj === Object(obj) && !Array.isArray(obj) && typeof obj !== "function"
  );
};

// Type guard: verifies an unknown value is a string[]
export const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === "string");

export const camelize = function <T>(input: T): T {
  return (function recurse<
    K extends Record<string, unknown> | Record<string, unknown>[] | unknown
  >(input: K): K {
    if (isObject(input)) {
      return Object.keys(input).reduce((previousValue, currentValue) => {
        return Object.assign(previousValue, {
          [toCamel(currentValue)]: recurse(input[currentValue]),
        });
      }, {} as Extract<K, Record<string, unknown>>);
    } else if (isArray(input)) {
      return input.map((i) => recurse(i)) as K;
    }
    return input;
  })(input);
};

export const handleize = function (s: string) {
  let string = s.toLowerCase();

  const toReplace = ['"', "'", "\\", "(", ")", "[", "]"];
  toReplace.forEach((char) => string.replace(char, ""));

  string = string.replace(/\W+/g, "-");

  if (string.charAt(string.length - 1) === "-") {
    string = string.replace(/-+z/, "");
  }

  if (string.charAt(0) === "-") {
    string = string.replace(/A-+/, "");
  }

  return string;
};

export const normalize = (s: string) => {
  // Normalize common diacritics
  return s
    .toLowerCase()
    .replace(/è/g, "e")
    .replace(/é/g, "e")
    .replace(/ê/g, "e")
    .replace(/à/g, "a")
    .replace(/â/g, "a")
    .replace(/î/g, "i")
    .replace(/ï/g, "i")
    .replace(/ô/g, "o")
    .replace(/ù/g, "u")
    .replace(/û/g, "u")
    .replace(/ü/g, "u")
    .replace(/ç/g, "c");
};

export const transformToParamKey = (string: string) => {
  return string.toLowerCase().replaceAll(" ", "_");
};

export const interpolateTranslationString = (
  text: string,
  variableValues: string[]
) => {
  let replacementIndex = 0;
  return text.replace(/\${[^}]+}/g, () => {
    const replacement = variableValues[replacementIndex];
    replacementIndex++;
    return replacement !== undefined ? replacement : "";
  });
};

/**
 * Interpolates `${token}` placeholders by name rather than by position.
 *
 * Prefer this over {@link interpolateTranslationString} whenever a template has
 * more than one placeholder: positional filling silently produces the wrong
 * string if a translator reorders the tokens.
 *
 * Values are substituted through a replacer function, so a `$`-sequence in a
 * value (a product title containing `$&`) is inserted verbatim rather than
 * treated as a replacement pattern. Only own properties are consulted, so a
 * placeholder named after an `Object.prototype` member (`${constructor}`) is
 * left as literal text instead of resolving to an inherited value.
 *
 * Only the `${token}` form is matched: the section templates already normalize
 * Liquid `{{ token }}` into `${token}` before hydration, so React never sees
 * the raw Liquid form.
 */
export const interpolateNamedTranslationString = (
  text: string,
  values: Record<string, string | number>
) =>
  text.replace(/\$\{\s*([^}\s]+)\s*\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(values, key)
      ? String(values[key])
      : match
  );

/**
 * A single segment produced by {@link parseTemplateString}.
 *
 * @typeParam K - Union of token kind literals (e.g. `"label" | "percent"`).
 *   The `"text"` kind is always included automatically for static parts of the
 *   template that sit between (or around) tokens.
 */
export type TemplateSegment<K extends string = string> = {
  kind: K | "text";
  value: string;
};

/**
 * A single named replacement consumed by {@link parseTemplateString}.
 *
 * The `key` should omit the `${` / `}` wrapper because the template remains the
 * source of truth for placeholder syntax. For example, `${label}` in the
 * template is matched by `{ key: "label", value: "Bundle & Save:" }`.
 */
type TemplateValue<K extends string = string> = {
  key: K;
  value: string;
};

/**
 * Parse a Liquid-style template string containing named `${token}` placeholders
 * into an ordered array of typed segments.
 *
 * Each segment carries a discriminant `kind` so consumers can apply per-token
 * rendering (bold, styled, etc.) without re-parsing. Unrecognized `${…}`
 * placeholders that are not present in `values` are left as literal text.
 *
 * For flat string interpolation that returns a plain string (no per-token
 * styling needed), use {@link interpolateTranslationString} instead.
 *
 * @typeParam K - Inferred from the `key` values in `values`. Determines the
 *   discriminant union on the returned segments.
 *
 * @param template - The raw template string from section settings, e.g.
 *   `"${label} ${percent} off by bundling with items in your cart."`.
 * @param values - Named replacements for placeholders found in `template`.
 *   Each `key` is converted into a `${key}` lookup internally. Example:
 *   ```ts
 *   [
 *     { key: "label", value: "Bundle & Save:" },
 *     { key: "percent", value: "15%" },
 *   ]
 *   ```
 * @returns An ordered array of {@link TemplateSegment} objects. Static text
 *   between tokens gets `kind: "text"`. Empty templates only return `[]` when
 *   token parsing runs; otherwise an empty template with no values is preserved
 *   as a single text segment so callers keep the original static string contract.
 *
 * @example
 * ```ts
 * parseTemplateString("Save ${percent} with the ${label}", [
 *   { key: "label", value: "Bundle & Save:" },
 *   { key: "percent", value: "15%" },
 * ]);
 * // [
 * //   { kind: "text",    value: "Save " },
 * //   { kind: "percent", value: "15%" },
 * //   { kind: "text",    value: " with the " },
 * //   { kind: "label",   value: "Bundle & Save:" },
 * // ]
 * ```
 */
export const parseTemplateString = <K extends string>(
  template: string,
  values: TemplateValue<K>[]
): TemplateSegment<K>[] => {
  const tokenEntries = values.map(
    (value) =>
      ["${" + value.key + "}", { kind: value.key, value: value.value }] as const
  );

  // No replacements provided — the entire template is static text.
  if (tokenEntries.length === 0) {
    return [{ kind: "text", value: template }];
  }

  const tokens = tokenEntries.reduce<
    Record<string, { kind: K; value: string }>
  >((accumulator, [token, value]) => {
    accumulator[token] = value;
    return accumulator;
  }, {});
  const tokenKeys = tokenEntries
    .map(([token]) => token)
    .sort((a, b) => b.length - a.length);

  // Escape regex-special characters in token strings (e.g. `$`, `{`, `}`)
  // so they match literally when used inside the split pattern.
  const escaped = tokenKeys.map((t) =>
    t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );

  // Build a capturing group that matches any known token. The capture group
  // is critical: String.prototype.split retains captured groups in the result
  // array, giving us both the static text *and* the matched token strings in
  // their original order.
  const pattern = new RegExp(`(${escaped.join("|")})`, "g");

  return template
    .split(pattern)
    .filter(Boolean) // drop empty strings from consecutive delimiters / edges
    .map<TemplateSegment<K>>((segment) => {
      // If this segment matches a known token key, emit a typed segment with
      // the token's kind and resolved value.
      const token = tokens[segment];
      if (token) {
        return { kind: token.kind, value: token.value };
      }
      // Otherwise it's static text between (or around) tokens.
      return { kind: "text", value: segment };
    });
};

export const validateEmailAddress = function (input: string) {
  return /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/gm.test(
    input
  );
};
